import Axios from 'axios';
import emitter from 'tiny-emitter/instance';
import PQueue from 'p-queue';
import store from 'src/store';
import {
  apiUrl,
  apiTimeout,
} from './config';

class ApiService {
  static serverErrorsCount = 0;
  static options = { headers: {} };
  static timeout = 5000;

  static createInstance() {
    if (this.instanceCreated) {
      return;
    }
    this.queues = [];

    this.axios = Axios.create({
      baseURL: apiUrl,
      timeout: apiTimeout,
      // validateStatus(status) {
      //   return statusIsCorrect(status);
      // },
    });

    /** interceptor for server response. These callbacks fire before any 'than' or 'catch' */
    this.axios.interceptors.response.use((response) => {
      if (response.data && typeof response.data.result !== 'undefined') {
        return response.data.result;
      }
      return response;
    }, error => Promise.reject(error));

    this.instanceCreated = true;
  }

   /** List of queues of requests to server. Key is name of request. Value is instance of PQueue. */
  static queues;
  static axios;

  /** Returns existing queue by name or creates, saves and returns new if doesn't exist */
  static getQueue(key) {
    if (!this.queues[key]) {
      this.queues[key] = new PQueue({ concurrency: 1 });
    }
    return this.queues[key];
  }

  static setApiTimeout(timeout) {
    this.axios.defaults.timeout = timeout;
  }

  /** Sets header for all requests */
  static setHeader(key, val) {
    this.options.headers[key] = val;
  }

  /**
   * Handler for server errors
   * */
  static handleError(error) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.status >= 400 && error.response.status < 500) {
        if (error.response.status === 401) {
          store.dispatch('userData/logOut');
        }
        // addToast(error.response.data && error.response.data.error);
      }
      if (error.response.data.error) {
        emitter.emit('error', error.response.data.error);
      }
      return Promise.reject({ ...error.response.data, tokenExpired: true });
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      // addToast('Server is not responding. Please try later');
      emitter.emit('error', 'Server is unavailable. Please, try later');
      return Promise.reject({ serverUnavailable: true });
    }
    // Something happened in setting up the request that triggered an Error
    // toast.error(`Server error: ${error.message}`);
    // addToast('Server is not responding. Please try later');
    emitter.emit('error', 'Something went wrong. Please, try later');
    return Promise.reject({ serverUnavailable: true });
  }

  static getRecentLogins(limit = 50, offset = 0) {
    return this.axios.get(`login-data?limit=${limit}&offset=${offset}`, this.options)
      .catch(this.handleError);
  }

  static getTickers(params) {
    const queue = this.getQueue(this.getTickers.name);
    return queue.add(() => this.axios.get('/tickers', {
      ...this.options,
      params,
    }).catch(this.handleError));
  }

  static getTickerByTickerId(ticker) {
    return this.axios.get(`/ticker/${ticker}`, this.options)
      .catch(this.handleError);
  }

  static getUserFavoriteTickers(params) {
    return this.axios.get('/tickers/favorite', {
      ...this.options,
      params,
    }).catch(this.handleError);
  }

  static addAlert(alert) {
    return this.axios.post('/alert', alert, this.options)
      .catch(this.handleError);
  }

  static updateAlert(id, alert) {
    return this.axios.put(`/alert/${id}`, alert, this.options)
      .catch(this.handleError);
  }

  static archiveAlert(id) {
    return this.axios.put(`/alert/${id}/archive`, this.options)
      .catch(this.handleError);
  }

  static deleteAlert(id) {
    return this.axios.delete(`/alert/${id}`, this.options)
      .catch(this.handleError);
  }

  static getAlerts() {
    return this.axios.get('/alerts', this.options)
      .catch(this.handleError);
  }

  static getAlertById(id) {
    return this.axios.get(`/alert/${id}`, this.options)
      .catch(this.handleError);
  }

  static signUp(email, password, confirmPassword) {
    return this.axios.post('/signup', { email, password, confirmPassword, timezone: '+0100' }, this.options)
      .catch(this.handleError);
  }

  static signIn(email, password, rememberMe) {
    return this.axios.post('/signin', { email, password, rememberMe }, this.options)
      .catch(this.handleError);
  }

  static forgotPassword(email) {
    return this.axios.post('/forgot-password', { email }, this.options)
      .catch(this.handleError);
  }

  static healthcheck(dontShowErrors) {
    return this.axios.get('/healthcheck', this.options)
      .catch(error => !dontShowErrors && this.handleError(error));
  }

  static resetPassword(newPassword, confirmNewPassword, token) {
    const data = { newPassword, confirmNewPassword, token };
    return this.axios.post('/reset-password', data, this.options)
      .catch(this.handleError);
  }

  static changePassword(data) {
    return this.axios.put('/user/password', data, this.options)
      .catch(this.handleError);
  }

  static changeEmail(form) {
    return this.axios.put('/user/email', form, this.options)
      .catch(this.handleError);
  }

  static confirmChangingEmail(token) {
    return this.axios.get(`/user/email-confirm?token=${token}`, this.options)
      .catch(this.handleError);
  }

  static getUserSettings() {
    return this.axios.get('/user/settings', this.options)
      .catch(this.handleError);
  }

  static changeEmailSubscriptionFrequency(subscriptionFrequency) {
    return this.axios.put(
        '/user/settings/subscription-frequency',
        { subscriptionFrequency },
        this.options,
      ).catch(this.handleError);
  }

  static changeDefaultFiatCurrency(currencyCode) {
    return this.axios.put(
        '/user/settings/currency',
        { currencyCode },
        this.options,
      ).catch(this.handleError);
  }

  static verifyToken(token) {
    return this.axios.post('/verify-token', { token }, this.options)
      .catch(this.handleError);
  }

  static confirmEmail(token) {
    return this.axios.get(`/confirm-email?token=${token}`, this.options)
      .catch(this.handleError);
  }

  static searchCurrency(kw, et, limit = 10) {
    return this.axios.get('/tickers/find', {
      ...this.options,
      params: {
        kw,
        et: et.join(','),
        limit,
      },
    })
      .catch(this.handleError);
  }

  static getNotifications(dontShowErrors) {
    const queue = this.getQueue(this.getNotifications.name);
    return queue.add(() => this.axios.get('/notifications', this.options)
      .catch(error => !dontShowErrors && this.handleError(error)));
  }

  static getNotificationsHistory(limit = 50, offset = 0) {
    return this.axios.get(`/notifications-history?limit=${limit}&offset=${offset}`, this.options)
      .catch(this.handleError);
  }

  static getGoogleOauthLink() {
    return this.axios.get('/oauth/google/url', this.options).catch(this.handleError);
  }

  static getFacebookOauthLink() {
    return this.axios.get('/oauth/facebook/url', this.options).catch(this.handleError);
  }

  static getLinkedInOauthLink() {
    return this.axios.get('/oauth/linkedin/url', this.options).catch(this.handleError);
  }

  static addTickerToFavorite(ticker) {
    return this.axios.post(`/ticker/${ticker}/favorite`, { ticker }, this.options)
      .catch(this.handleError);
  }

  static removeTickerFromFavorite(ticker) {
    return this.axios.post(`/ticker/${ticker}/unfavorite`, { ticker }, this.options)
      .catch(this.handleError);
  }
  static getAlertsGroupedByTickers() {
    return this.axios.get('/alerts/grouped-by-ticker', this.options)
    .catch(this.handleError);
  }

  static getAlertsByTicker(tickerID) {
    return this.axios.get(`/alerts/by-ticker/${tickerID}`, this.options)
    .catch(this.handleError);
  }

  static sendMessageToSupport(supportData) {
    return this.axios.post('/support/request', supportData, this.options)
      .catch(this.handleError);
  }

  static getConditions() {
    return this.axios.get('/alert-conditions', this.options)
      .catch(this.handleError);
  }

  static getPreviewMessageAlertCondition(form) {
    return this.axios.post('/alert-condition/preview', form, this.options)
      .catch(this.handleError);
  }

  static createAlert(form) {
    return this.axios.post('/alert', form, this.options)
      .catch(this.handleError);
  }

  static udpateUserCurrencies(tickerIDs) {
    return this.axios.put('/user/ticker-subscriptions', { tickerIDs }, this.options)
      .catch(this.handleError);
  }

  static getUserConfirmationStatus() {
    return this.axios.get('/user/confirmation-status', this.options)
      .catch(this.handleError);
  }

  static getNumberNewCurrencies() {
    return this.axios.get('/tickers/created-in-24h', this.options)
    .catch(this.handleError);
  }

  static changePhoneNumber(phone) {
    return this.axios.post('/user/settings/add-phone-candidate', { phone }, this.options)
    .catch(this.handleError);
  }

  static deletePhone() {
    return this.axios.delete('user/settings/phone', this.options)
    .catch(this.handleError);
  }

  static confirmPhoneNumber(phone, code) {
    return this.axios.post('/user/settings/confirm-phone-changing', { phone, code }, this.options)
    .catch(this.handleError);
  }

  static changeTimezone(timezone) {
    return this.axios.put('/user/timezone', { timezone }, this.options)
    .catch(this.handleError);
  }

  static getMostChangedTickers() {
    return this.axios.get('/tickers/most-changed', this.options)
    .catch(this.handleError);
  }

  static getRecentlyAdded() {
    return this.axios.get('/tickers/recently-added', this.options)
    .catch(this.handleError);
  }

  /* Notifications in side menu(not about alert) */
  static getUnreadUserNotifications(dontShowErrors) {
    return this.axios.get('/user/notifications', this.options)
      .catch(error => !dontShowErrors && this.handleError(error));
  }

  static removeUserNotification(notificationKey) {
    return this.axios.put('/user/notifications', {
      notificationKey,
    }, this.options)
    .catch(this.handleError);
  }

  static getUserQuotasInfo() {
    return this.axios.get('/user/quotas-info', this.options)
    .catch(this.handleError);
  }

  static getPaymentHistory(limit, offset) {
    return this.axios.get('/payment/history', {
      ...this.options,
      params: {
        limit,
        offset,
      },
    })
    .catch(this.handleError);
  }

  static getPaymentCardInfo() {
    return this.axios.get('/payment/card', this.options)
    .catch(this.handleError);
  }

  static updatePaymentCard(stripeToken) {
    return this.axios.post('/payment/update-card', { stripeToken }, this.options)
    .catch(this.handleError);
  }

  static removePaymentCard() {
    return this.axios.delete('/payment/card', this.options)
    .catch(this.handleError);
  }

  static declineTariffPlanChanging() {
    return this.axios.put('/subscription/decline-changing', this.options)
    .catch(this.handleError);
  }

  static getTariffs() {
    return this.axios.get('/tariffs', this.options)
    .catch(this.handleError);
  }

  static getCurrentTariff() {
    return this.axios.get('/user/tariff', this.options)
    .catch(this.handleError);
  }

  static sendSubscriptionEmail(target, email) {
    return this.axios.post('/subscriptions/send', {
      target,
      email,
    }, this.options)
    .catch(this.handleError);
  }

  static getAdminTariffsList() {
    return this.axios.get('/admin/tariffs', this.options)
    .catch(this.handleError);
  }

  static updateTariff(id, tariff) {
    return this.axios.put(`/admin/tariffs/${id}`, tariff, this.options)
    .catch(this.handleError);
  }

  static deleteTariff(id) {
    return this.axios.delete(`/admin/tariffs/${id}`, this.options)
    .catch(this.handleError);
  }

  static activateTariff(id) {
    return this.axios.put(`/admin/tariffs/${id}/activate`, this.options)
    .catch(this.handleError);
  }

  static createTariff(tariff) {
    return this.axios.post('/admin/tariffs/new', tariff, this.options)
    .catch(this.handleError);
  }

  static findUsers(field, kw, offset, limit) {
    return this.axios.get('/user/find', {
      ...this.options,
      params: {
        field,
        kw,
        offset,
        limit,
      },
    }).catch(this.handleError);
  }

  static getUserDetails(id) {
    return this.axios.get(`/admin/user/${id}`, this.options)
      .catch(this.handleError);
  }

  static changeTariffPlan(tariffPlanID) {
    return this.axios.post('/payment/change-plan', { tariffPlanID }, this.options)
    .catch(this.handleError);
  }

  static deleteAccount(keepEmailSubscription) {
    return this.axios.delete('/user', {
      ...this.options,
      params: {
        keepEmailSubscription,
      },
    }).catch(this.handleError);
  }

  static dismissPlatformNotifications(notificationsIDs) {
    return this.axios.put('/notifications/dismiss', { notificationsIDs }, this.options).catch(this.handleError);
  }

  static getRevision() {
    return this.axios.get('/revision', this.options).catch(this.handleError);
  }

  static getTickerAvailablePeriods(tickerId) {
    return this.axios.get(`/ticker/${tickerId}/available-data-periods`, this.options)
      .catch(this.handleError);
  }

  static getChartBigData({ range, chartType, currencyCode, tickerId }) {
    return this.axios.post('/tickers/graph-data', { range, chartType, currencyCode, tickerId }, this.options).catch(this.handleError);
  }

  static updateEmailSubscription(emailSubscription) {
    return this.axios.put('/user/settings/email-subscription', { emailSubscription }, this.options)
      .catch(this.handleError);
  }
}

ApiService.createInstance();
export default ApiService;



// WEBPACK FOOTER //
// ./src/utils/apiService.js